import { useEffect } from 'react';
import { hasConsentFor } from '@/utils/cookieConsent';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

/**
 * useLandingAnalytics Hook
 *
 * Tracks user interactions on landing pages for analytics.
 * Can be integrated with Google Analytics, Mixpanel, or other analytics platforms.
 *
 * @example
 * const { trackPageView, trackEvent, trackCTAClick } = useLandingAnalytics('for-salons');
 *
 * // Track CTA clicks
 * <Button onClick={() => trackCTAClick('hero-primary')}>
 *   Get Started
 * </Button>
 */
export function useLandingAnalytics(pageName: string) {
  // Track page view on mount
  useEffect(() => {
    trackPageView();
  }, [pageName]);

  /**
   * Track page view
   */
  const trackPageView = () => {
    // Only track if user has consented to analytics
    if (!hasConsentFor('analytics')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Skipped (no consent):', { page: pageName });
      }
      return;
    }

    // Integration point for analytics platforms
    if (typeof window !== 'undefined') {
      // Google Analytics 4
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: pageName,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });
      }

      // Development logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Page View:', {
          page: pageName,
          url: window.location.href,
        });
      }
    }
  };

  /**
   * Track custom event
   */
  const trackEvent = ({ category, action, label, value }: AnalyticsEvent) => {
    // Only track if user has consented to analytics
    if (!hasConsentFor('analytics')) {
      return;
    }

    if (typeof window !== 'undefined') {
      // Google Analytics 4
      if (window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value,
        });
      }

      // Development logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Event:', {
          category,
          action,
          label,
          value,
          page: pageName,
        });
      }
    }
  };

  /**
   * Track CTA button clicks
   */
  const trackCTAClick = (ctaLocation: string, ctaText?: string) => {
    trackEvent({
      category: 'CTA',
      action: 'click',
      label: `${pageName}:${ctaLocation}${ctaText ? `:${ctaText}` : ''}`,
    });
  };

  /**
   * Track section visibility (scroll tracking)
   */
  const trackSectionView = (sectionName: string) => {
    trackEvent({
      category: 'Section View',
      action: 'scroll',
      label: `${pageName}:${sectionName}`,
    });
  };

  /**
   * Track video plays
   */
  const trackVideoPlay = (videoName: string) => {
    trackEvent({
      category: 'Video',
      action: 'play',
      label: `${pageName}:${videoName}`,
    });
  };

  /**
   * Track form submissions
   */
  const trackFormSubmit = (formName: string) => {
    trackEvent({
      category: 'Form',
      action: 'submit',
      label: `${pageName}:${formName}`,
    });
  };

  /**
   * Track outbound links
   */
  const trackOutboundLink = (destination: string) => {
    trackEvent({
      category: 'Outbound Link',
      action: 'click',
      label: destination,
    });
  };

  /**
   * Track high-value conversions across GA4 + Meta Pixel + TikTok.
   * Use these at the boundaries of the funnel, not for generic clicks.
   */
  const trackLead = (source?: string) => {
    if (!hasConsentFor('analytics')) return;
    window.gtag?.('event', 'generate_lead', { source, page: pageName });
    window.fbq?.('track', 'Lead', { source });
    window.ttq?.track('Lead', { source });
  };

  const trackCompleteRegistration = (plan?: string) => {
    if (!hasConsentFor('analytics')) return;
    window.gtag?.('event', 'sign_up', { method: 'email', plan });
    window.fbq?.('track', 'CompleteRegistration', { content_name: plan });
    window.ttq?.track('CompleteRegistration', { content_name: plan });
  };

  const trackStartTrial = (plan: string, value?: number, currency?: string) => {
    if (!hasConsentFor('analytics')) return;
    window.gtag?.('event', 'start_trial', { plan, value, currency });
    window.fbq?.('track', 'StartTrial', { value, currency, predicted_ltv: value });
    window.ttq?.track('StartTrial', { value, currency, content_name: plan });
  };

  const trackSubscribe = (plan: string, value: number, currency: string) => {
    if (!hasConsentFor('analytics')) return;
    window.gtag?.('event', 'purchase', {
      transaction_id: `${Date.now()}-${plan}`,
      value,
      currency,
      items: [{ item_id: plan, item_name: plan, price: value, quantity: 1 }],
    });
    window.fbq?.('track', 'Subscribe', { value, currency, predicted_ltv: value * 12 });
    window.ttq?.track('Subscribe', { value, currency, content_name: plan });
  };

  return {
    trackPageView,
    trackEvent,
    trackCTAClick,
    trackSectionView,
    trackVideoPlay,
    trackFormSubmit,
    trackOutboundLink,
    trackLead,
    trackCompleteRegistration,
    trackStartTrial,
    trackSubscribe,
  };
}
